import { Loading } from "../app/Status";
import { PageHeader } from "../app/PageHeader";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import {
  CHANGELOG_KIND_LABELS,
  formatTicketNumber,
  SUPPORT_ATTACHMENT_ACCEPT,
  TICKET_COMPONENT_LABELS,
  TICKET_COMPONENTS,
  TICKET_KIND_LABELS,
  TICKET_KINDS,
  type BuildingDto,
  type ChangelogAppResponse,
  type CustomerTicketDetail,
  type TicketContext,
  type TicketKind,
} from "@klantpunt/shared";
import { IconChevronLeft, IconPlus } from "../app/icons";
import { ParentCompany } from "../app/ParentCompany";
import { changelogAppKey, useChangelogApp, useKbIndex, useKbPublished, useKbSearch } from "../features/helpcenter/api";
import "../features/helpcenter/helpcenter.css";
import { StatusLine } from "../features/status/StatusBanner";
import { api } from "../lib/api";
import { buildingKey } from "../features/building/useBuilding";
import {
  errMsg,
  fmtDateTime,
  myTicketKey,
  myTicketsKey,
  postForm,
  useMyTicket,
  useMyTickets,
} from "../features/support/api";
import { Thread } from "../features/support/Thread";
import { useSupportEvents } from "../features/support/useSupportEvents";
import "../features/support/support.css";
import { useMe } from "../lib/auth";
import { useToast } from "../lib/toast";

/** Context bij een bericht uit de app (docs/HELPCENTER.md §4.4), afgeleid van het scherm waar Hulp werd geopend. */
export function appContext(from: string, qc: QueryClient): TicketContext {
  const m = /^\/gebouwen\/([0-9a-f-]{36})/.exec(from);
  const building = m ? qc.getQueryData<BuildingDto>(buildingKey(m[1]!)) : undefined;
  const floorId = new URLSearchParams(from.split("?")[1] ?? "").get("verdieping");
  const floor = floorId ? building?.floors?.find((f) => f.id === floorId) : undefined;
  return {
    route: from || null,
    buildingId: m?.[1] ?? null,
    buildingName: building ? `${building.code} · ${building.name}` : null,
    floorId: floor?.id ?? null,
    floorName: floor?.name ?? null,
    appVersion: import.meta.env.VITE_APP_VERSION ?? "dev",
    userAgent: navigator.userAgent.slice(0, 500),
    online: navigator.onLine,
  };
}

/** Hulp: kennisbank, eigen berichten en een nieuw bericht. */
export function HelpPage() {
  const q = useMyTickets();
  const [params] = useSearchParams();
  const from = params.get("vanaf") ?? "";
  useSupportEvents("customer");
  const list = q.data ?? [];
  return (
    <div className="page narrow help">
      <PageHeader
        label="Hulp"
        title="Hoe kunnen we helpen"
        action={
          <Link to={`/hulp/nieuw${from ? `?vanaf=${encodeURIComponent(from)}` : ""}`} className="btn primary">
            <IconPlus size={20} aria-hidden="true" />
            Nieuw bericht
          </Link>
        }
      />
      <StatusLine />
      <KnowledgeBase />
      <WhatsNew />
      <section className="panel" aria-labelledby="mine-h">
        <h2 id="mine-h">Mijn berichten</h2>
        {q.isPending && <Loading />}
        {!q.isPending && list.length === 0 && <p className="muted">Je hebt nog geen berichten aan support gestuurd.</p>}
        <ul className="sp-mylist">
          {list.map((t) => (
            <li key={t.id}>
              <Link to={`/hulp/berichten/${t.id}`} className="sp-myrow">
                <span className="code small">{formatTicketNumber(t.number)}</span>
                <span className="grow">{t.subject}</span>
                <span className={`chip sp-st${t.waitingOnCustomer ? " sp-st-wait" : ` sp-st-${t.status}`}`}>
                  {t.statusLabel}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </section>
      <ParentCompany />
    </div>
  );
}

/** Nieuw in Klantpunt (§6): updates met "Ook tonen in de app"; bekijken haalt de stip bij Hulp weg. */
function WhatsNew() {
  const qc = useQueryClient();
  const q = useChangelogApp();
  const unseen = q.data?.unseen ?? false;
  useEffect(() => {
    if (!unseen) return;
    void api("/help/changelog/seen", { method: "POST" })
      .then(() => qc.setQueryData<ChangelogAppResponse>(changelogAppKey, (d) => (d ? { ...d, unseen: false } : d)))
      .catch(() => {});
  }, [unseen, qc]);
  const items = q.data?.items ?? [];
  if (!items.length) return null;
  return (
    <section className="panel" aria-labelledby="news-h">
      <h2 id="news-h">Nieuw in Klantpunt</h2>
      <ul className="kb-news">
        {items.map((e) => (
          <li key={e.id}>
            <details>
              <summary>
                <span className={`chip kb-kind-${e.kind}`}>{CHANGELOG_KIND_LABELS[e.kind]}</span> {e.title}{" "}
                <time className="muted small" dateTime={e.date}>
                  {new Date(`${e.date}T12:00:00`).toLocaleDateString("nl-NL", { day: "numeric", month: "long" })}
                </time>
              </summary>
              <div className="kb-article" dangerouslySetInnerHTML={{ __html: e.html }} />
              {e.articleSlug && <Link to={`/hulp/artikel/${e.articleSlug}`}>Lees hoe het werkt</Link>}
            </details>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Kennisbank in Hulp: zoeken en de artikelen per categorie, ook die alleen in de app staan. */
function KnowledgeBase() {
  const idx = useKbIndex();
  const [q, setQ] = useState("");
  const term = q.trim();
  const hits = useKbSearch(term);
  const cats = idx.data?.categories ?? [];
  if (idx.isSuccess && cats.length === 0) return null;
  return (
    <section className="panel" aria-labelledby="kb-h">
      <h2 id="kb-h">Kennisbank</h2>
      <input
        type="search"
        className="kb-search"
        placeholder="Zoek in de kennisbank, bijvoorbeeld QR-labels"
        aria-label="Zoek in de kennisbank"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      {term.length >= 2 ? (
        <ul className="kb-hits" aria-live="polite">
          {hits.data?.length === 0 && <li className="muted">Niets gevonden. Stuur ons gerust een bericht.</li>}
          {(hits.data ?? []).map((h) => (
            <li key={h.slug}>
              <Link to={`/hulp/artikel/${h.slug}`}>{h.title}</Link>{" "}
              <span className="muted small">{h.categoryTitle}</span>
              <p className="muted small">{h.excerpt}</p>
            </li>
          ))}
        </ul>
      ) : (
        <ul className="kb-cats">
          {cats.map((c) => (
            <li key={c.id} className="kb-cat">
              <h3>{c.title}</h3>
              <ul>
                {c.articles.map((a) => (
                  <li key={a.slug}>
                    <Link to={`/hulp/artikel/${a.slug}`}>{a.title}</Link>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export function HelpArticlePage() {
  const { slug } = useParams();
  const q = useKbPublished(slug);
  const [sent, setSent] = useState<"ja" | "nee" | null>(null);
  const [no, setNo] = useState(false);
  const [comment, setComment] = useState("");
  const a = q.data;

  async function feedback(helpful: "ja" | "nee") {
    try {
      await api("/public/help/feedback", { method: "POST", body: { article: slug, helpful, comment } });
    } catch {
      /* feedback is niet belangrijk genoeg voor een foutmelding */
    }
    setSent(helpful);
  }

  return (
    <div className="page help">
      <Link to="/hulp" className="backlink">
        <IconChevronLeft size={18} aria-hidden="true" />
        Hulp
      </Link>
      {q.isPending && <Loading />}
      {q.isError && <p className="muted">Dit artikel bestaat niet (meer).</p>}
      {a && (
        <>
          <p className="label">{a.category.title}</p>
          <h1 className="dot">{a.title}</h1>
          <article className="kb-article" dangerouslySetInnerHTML={{ __html: a.html }} />
          <section className="panel kb-feedback" aria-labelledby="kbf-h">
            <h2 id="kbf-h">Was dit artikel nuttig?</h2>
            {sent ? (
              <p role="status">Bedankt voor je reactie.</p>
            ) : no ? (
              <>
                <label className="field">
                  <span>Wat miste je? (optioneel)</span>
                  <textarea rows={3} maxLength={2000} value={comment} onChange={(e) => setComment(e.target.value)} />
                </label>
                <div className="btnrow">
                  <button type="button" className="btn primary" onClick={() => void feedback("nee")}>
                    Versturen
                  </button>
                  <Link to="/hulp/nieuw" className="btn">
                    Vraag het support
                  </Link>
                </div>
              </>
            ) : (
              <div className="btnrow">
                <button type="button" className="btn" onClick={() => void feedback("ja")}>
                  Ja
                </button>
                <button type="button" className="btn" onClick={() => setNo(true)}>
                  Nee
                </button>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

export function HelpNewPage() {
  const { data: me } = useMe();
  const [params] = useSearchParams();
  const from = params.get("vanaf") ?? "";
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [kind, setKind] = useState<TicketKind>("question");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ctx = appContext(from, qc);

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    form.set("context", JSON.stringify(ctx));
    setBusy(true);
    setError(null);
    try {
      const t = await postForm<CustomerTicketDetail>("/help/tickets", form);
      await qc.invalidateQueries({ queryKey: myTicketsKey });
      toast(`Verstuurd als ${formatTicketNumber(t.number)}`);
      navigate(`/hulp/berichten/${t.id}`, { replace: true });
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  }

  const shown: [string, string | null | undefined][] = [
    ["Organisatie", me?.org?.name],
    ["Gebruiker", me?.user.name ?? me?.user.email],
    ["Gebouw", ctx.buildingName],
    ["Verdieping", ctx.floorName],
    ["Scherm", ctx.route],
    ["Versie", ctx.appVersion],
    ["Verbinding", ctx.online ? "Online" : "Offline"],
    ["Browser", ctx.userAgent],
  ];

  return (
    <div className="page help">
      <Link to="/hulp" className="backlink">
        <IconChevronLeft size={18} aria-hidden="true" />
        Hulp
      </Link>
      <h1 className="dot">Nieuw bericht</h1>
      <StatusLine />
      <form className="panel sp-form" onSubmit={submit}>
        <fieldset className="field">
          <legend>Waar gaat het over?</legend>
          <div className="segmented" role="radiogroup" aria-label="Soort bericht">
            {TICKET_KINDS.map((k) => (
              <label key={k} className="segbtn">
                <input
                  type="radio"
                  name="kind"
                  value={k}
                  checked={kind === k}
                  onChange={() => setKind(k)}
                  className="sr-only"
                />
                {TICKET_KIND_LABELS[k]}
              </label>
            ))}
          </div>
        </fieldset>
        {kind === "problem" && (
          <label className="field">
            <span>Welk onderdeel?</span>
            <select name="component" defaultValue="">
              <option value="">Kies een onderdeel</option>
              {TICKET_COMPONENTS.map((c) => (
                <option key={c} value={c}>
                  {TICKET_COMPONENT_LABELS[c]}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="field">
          <span>Onderwerp</span>
          <input name="subject" required maxLength={160} />
        </label>
        <label className="field">
          <span>Bericht</span>
          <textarea name="body" required rows={6} maxLength={20000} />
        </label>
        <label className="field">
          <span>Schermafbeelding of bijlage (optioneel)</span>
          <input type="file" name="attachment" accept={SUPPORT_ATTACHMENT_ACCEPT} />
          <span className="hint">PNG, JPG of PDF, maximaal 10 MB.</span>
        </label>
        <details className="sp-ctx" open>
          <summary>Dit gaat mee met je bericht</summary>
          <dl>
            {shown
              .filter(([, v]) => v)
              .map(([k, v]) => (
                <div key={k}>
                  <dt>{k}</dt>
                  <dd>{v}</dd>
                </div>
              ))}
          </dl>
        </details>
        {error && (
          <p className="warn" role="alert">
            {error}
          </p>
        )}
        <div className="btnrow">
          <button type="submit" className="btn primary" disabled={busy}>
            {busy ? "Versturen…" : "Versturen"}
          </button>
        </div>
      </form>
    </div>
  );
}

export function HelpTicketPage() {
  const { id } = useParams();
  const q = useMyTicket(id);
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useSupportEvents("customer");
  const t = q.data;

  async function reply(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const el = e.currentTarget;
    setBusy(true);
    setError(null);
    try {
      const d = await postForm<CustomerTicketDetail>(`/help/tickets/${id}/messages`, new FormData(el));
      qc.setQueryData(myTicketKey(id!), d);
      void qc.invalidateQueries({ queryKey: myTicketsKey });
      el.reset();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page help">
      <Link to="/hulp" className="backlink">
        <IconChevronLeft size={18} aria-hidden="true" />
        Mijn berichten
      </Link>
      {q.isPending && <Loading />}
      {q.isError && <p className="muted">Dit bericht bestaat niet.</p>}
      {t && (
        <>
          <p className="label">
            {formatTicketNumber(t.number)} · {TICKET_KIND_LABELS[t.kind]} · {fmtDateTime(t.createdAt)}
          </p>
          <div className="titlerow">
            <h1 className="dot">{t.subject}</h1>
            <span className={`chip sp-st${t.waitingOnCustomer ? " sp-st-wait" : ` sp-st-${t.status}`}`}>
              {t.statusLabel}
            </span>
          </div>
          {t.waitingOnCustomer && <p className="note">Support wacht op jouw reactie.</p>}
          <Thread messages={t.messages} view="customer" />
          <form className="panel sp-form" onSubmit={reply}>
            <label className="field">
              <span>{t.status === "done" ? "Reageren (heropent het bericht)" : "Reageren"}</span>
              <textarea name="body" required rows={4} maxLength={20000} />
            </label>
            <label className="field">
              <span>Bijlage (optioneel)</span>
              <input type="file" name="attachment" accept={SUPPORT_ATTACHMENT_ACCEPT} />
            </label>
            {error && (
              <p className="warn" role="alert">
                {error}
              </p>
            )}
            <button type="submit" className="btn primary" disabled={busy}>
              {busy ? "Versturen…" : "Versturen"}
            </button>
          </form>
        </>
      )}
    </div>
  );
}
